import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { writePageParam } from '@/components/DataTable/pageParam';
import { useAuth } from '@/store/auth/auth.store';
import type { TaskFilters } from '@/store/tasks/task-filters';

import { EVERYONE } from './AssigneeCut';
import { PRIORITY_CUT_OPTIONS, STATUS_CUT_OPTIONS } from './task-cuts';
import { defaultTaskFilters } from './tasks-filter';

/**
 * **The Tasks list's filter lives in the URL.**
 *
 * Every axis of the cut is a search param, which is what makes Back undo the
 * filter instead of leaving the page, a reload land where you were, and
 * "everything assigned to Elena" a link you can send someone — the crew page
 * sends exactly that one. Held in `useState`, the cut was invisible to the
 * address bar and unreachable from anywhere else in the app.
 *
 * Both surfaces read it through this hook, so the desktop table and the mobile
 * list cannot drift on what a param means.
 */

export const ASSIGNEE_PARAM = 'assignee';
export const STATUS_PARAM = 'status';
export const PRIORITY_PARAM = 'priority';
export const TYPE_PARAM = 'type';
export const FROM_PARAM = 'from';
export const TO_PARAM = 'to';

/**
 * `EVERYONE` is the empty string — the absence of a `responsible` filter — and
 * the All cut is an empty status list. Neither can be written as a param value,
 * and neither can be left out either, because leaving a param out is how the
 * landing cut is spelled. So both get a word.
 */
export const EVERYONE_VALUE = 'everyone';
export const ALL_STATUSES_VALUE = 'all';

const STATUS_VALUES = new Set<string>(STATUS_CUT_OPTIONS.map(({ value }) => value));
const PRIORITY_VALUES = new Set<string>(PRIORITY_CUT_OPTIONS.map(({ value }) => value));

/** Everything assigned to one person, in whatever state — the crew page's link. */
export const tasksForAssigneeHref = (personId: string): string =>
  `/tasks?${ASSIGNEE_PARAM}=${encodeURIComponent(personId)}&${STATUS_PARAM}=${ALL_STATUSES_VALUE}`;

/**
 * Read defensively: a value this list cannot honour falls back to the landing
 * cut rather than being passed to the service, which would answer 400 for a
 * URL somebody merely mistyped.
 */
const readFilters = (params: URLSearchParams, currentUserId?: string): TaskFilters => {
  const defaults = defaultTaskFilters(currentUserId);

  const assignee = params.get(ASSIGNEE_PARAM);
  const status = params.get(STATUS_PARAM);
  const priority = params.get(PRIORITY_PARAM);

  return {
    startDate: params.get(FROM_PARAM) ?? defaults.startDate,
    endDate: params.get(TO_PARAM) ?? defaults.endDate,
    type: params.get(TYPE_PARAM) ?? defaults.type,
    status:
      status === null
        ? defaults.status
        : status === ALL_STATUSES_VALUE
          ? []
          : STATUS_VALUES.has(status)
            ? [status]
            : defaults.status,
    priority: priority && PRIORITY_VALUES.has(priority) ? priority : defaults.priority,
    responsible:
      assignee === null ? defaults.responsible : assignee === EVERYONE_VALUE ? EVERYONE : assignee,
    // The Tasks page is the non-equipment list; the equipment cut is the
    // service's (`equipment_id=none`), not something the URL chooses.
    equipment: defaults.equipment,
  };
};

/**
 * The two axes the list always has, stated outright.
 *
 * `status` and `assignee` are never absent: the landing cut is `pending` for
 * whoever is signed in, and leaving that implicit meant the address bar showed
 * no filter while the list was very much filtered — you could not tell from the
 * URL whether you were looking at your open tasks or at everything. The
 * optional axes below are different: `priority` and the dates are genuinely
 * *no* filter when unset, and an absent param is the honest way to say that.
 */
const REQUIRED_PARAMS = [STATUS_PARAM, ASSIGNEE_PARAM];

/** Whether the URL already states the cut, or still has to be told it. */
const isCanonical = (params: URLSearchParams): boolean =>
  REQUIRED_PARAMS.every((key) => params.has(key));

const writeFilters = (params: URLSearchParams, filters: TaskFilters): void => {
  const put = (key: string, value: string | undefined) => {
    if (value) params.set(key, value);
    else params.delete(key);
  };

  put(FROM_PARAM, filters.startDate);
  put(TO_PARAM, filters.endDate);
  put(TYPE_PARAM, filters.type);
  put(PRIORITY_PARAM, filters.priority);

  const [cut] = filters.status;
  params.set(STATUS_PARAM, cut ?? ALL_STATUSES_VALUE);
  params.set(ASSIGNEE_PARAM, filters.responsible || EVERYONE_VALUE);
};

export const useTaskFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * The signed-in user. `ProtectedRoute` renders nothing until the session has
   * settled and bounces anyone without one, so by the time this route mounts
   * this is stable — there is no window in which the landing cut would be
   * written for the wrong person.
   */
  const currentUserId = useAuth((state) => state.session?.id);

  const filters = useMemo(
    () => readFilters(searchParams, currentUserId),
    [searchParams, currentUserId],
  );
  /**
   * A new cut starts at its first page: page 4 of the old filter is not page 4 of the
   * new one, and landing on an empty tail of a list you have just narrowed reads as the
   * filter having found nothing. The page is `usePagedList`'s param, written here rather
   * than by the list, so that the cut and the page travel in one navigation.
   */

  const setFilters = useCallback(
    (next: TaskFilters | ((prev: TaskFilters) => TaskFilters)) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        // Derived from `prev` rather than from the render's `filters`, so a
        // second change in the same tick builds on the first.
        const value = typeof next === 'function' ? next(readFilters(params, currentUserId)) : next;
        writeFilters(params, value);
        writePageParam(params, 1);

        return params;
      });
    },
    [setSearchParams, currentUserId],
  );

  return { filters, setFilters };
};

/**
 * The search string a non-canonical `/tasks` URL should be corrected to, or null
 * when it already states its cut — `/tasks` becomes
 * `/tasks?status=pending&assignee=<me>`.
 *
 * Rendered as `<Navigate replace />` by `TasksPage`, not written from an effect.
 * Two reasons. The list must not be *mounted* under a filter that is about to
 * change: an effect fires after its children have already rendered and asked for
 * a page of tasks, so the page spent a request on a question nobody put and then
 * immediately asked again. And `setSearchParams` cannot be called during render
 * to get ahead of it — it goes through `navigate`, which bails out (`if
 * (!activeRef.current) return`) until its own layout effect has run, so a
 * render-time call would warn and silently do nothing on the one render that
 * matters.
 *
 * `replace`, because the reader never chose the bare URL: it does not deserve a
 * history entry, and Back still undoes exactly one real step. A deep link that
 * already carries both params — the crew page's, for one — is canonical on
 * arrival and gets no redirect, which is also what makes this loop-proof: the
 * correction's own output is canonical by construction.
 */
export const useCanonicalTaskSearch = (): string | null => {
  const [searchParams] = useSearchParams();
  const currentUserId = useAuth((state) => state.session?.id);

  return useMemo(() => {
    if (isCanonical(searchParams)) return null;
    const params = new URLSearchParams(searchParams);
    writeFilters(params, readFilters(params, currentUserId));
    return `?${params}`;
  }, [searchParams, currentUserId]);
};
